/**
 * MobileTeamsDetailsModal
 *
 * Full-screen MUI Dialog with Slide-up transition replacing the PrimeReact
 * right-panel Dialog in TeamsDetailsModal.
 * Sticky AppBar with team name and close button, scrollable form body,
 * sticky bottom action buttons (Deactivate + Save).
 */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Langs, { Localize } from "../../../app/lang/langs";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import { editTeam, getTeams, changeTeamActiveStatus } from "../../../components/teams/teamsSlice";
import { updateTeamSettings } from "../../settings/settingsSlice";
import { AssetsPannel } from "../../../components/inputs/AssetsPannel";
import { contractsLocationsOptions } from "../../../components/collabs/collabsSlice";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "14px";

const fieldLabelSx = {
    fontWeight: 700,
    mb: 0.75,
    letterSpacing: "0.04em",
    color: "rgba(0,0,0,0.45)",
    fontSize: "0.72rem",
    textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
        borderRadius: BORDER_RADIUS_INPUT,
        backgroundColor: "rgba(255,255,255,0.5)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        transition: TRANSITION_SMOOTH,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
        "& fieldset": {
            borderColor: "rgba(255,144,21,0.08)",
            transition: TRANSITION_SMOOTH,
        },
        "&:hover fieldset": {
            borderColor: "rgba(255,144,21,0.2)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(255,144,21,0.5)",
            boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
        },
        "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.7)",
            boxShadow: "0 4px 16px rgba(255,144,21,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
        },
    },
    "& input": {
        fontSize: "16px",
        transition: TRANSITION_SMOOTH,
    },
    "& textarea": {
        fontSize: "16px",
    },
    "& input::placeholder, & textarea::placeholder": {
        opacity: 0.4,
    },
};

const glassCardSx = {
    background: "rgba(255,255,255,0.65)",
    backdropFilter: "blur(20px) saturate(1.3)",
    WebkitBackdropFilter: "blur(20px) saturate(1.3)",
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.5)",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.7)",
    p: 2.5,
    mb: 2,
    transition: TRANSITION_SMOOTH,
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ---- Stepper for numeric fields ---- */
function NumberStepper({ value, onChange, step = 1, min, max, maxFractionDigits = 2, currency }) {
    const displayVal = value != null ? value : 0;

    const decrement = () => {
        let next = parseFloat((displayVal - step).toFixed(maxFractionDigits));
        if (min != null && next < min) next = min;
        onChange(next);
    };

    const increment = () => {
        let next = parseFloat((displayVal + step).toFixed(maxFractionDigits));
        if (max != null && next > max) next = max;
        onChange(next);
    };

    const formatDisplay = () => {
        if (value == null) return "";
        if (currency) {
            return new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: currency,
                minimumFractionDigits: 2,
            }).format(value);
        }
        return String(value);
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: "16px",
                border: "1px solid rgba(255,144,21,0.08)",
                overflow: "hidden",
                transition: TRANSITION_SMOOTH,
                bgcolor: "rgba(255,255,255,0.5)",
                backdropFilter: "blur(12px)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
                "&:focus-within": {
                    borderColor: "rgba(255,144,21,0.5)",
                    boxShadow: "0 0 0 3px rgba(255,144,21,0.1), inset 0 1px 0 rgba(255,255,255,0.8)",
                },
            }}
        >
            <IconButton
                onClick={decrement}
                sx={{
                    borderRadius: 0,
                    background: "linear-gradient(135deg, #FF9015 0%, #ff7b00 100%)",
                    color: "#fff",
                    width: 48,
                    height: 50,
                    transition: TRANSITION_SMOOTH,
                    "&:hover": { background: "linear-gradient(135deg, #ff7b00 0%, #FF9015 100%)" },
                    "&:active": { transform: "scale(0.95)" },
                }}
            >
                <RemoveIcon fontSize="small" />
            </IconButton>
            <TextField
                variant="standard"
                value={formatDisplay()}
                onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9.,\-]/g, "").replace(",", ".");
                    const parsed = parseFloat(raw);
                    if (!isNaN(parsed)) onChange(parsed);
                }}
                InputProps={{ disableUnderline: true }}
                inputProps={{ style: { textAlign: "center", fontSize: "16px", fontWeight: 600 } }}
                sx={{ flex: 1, "& input": { px: 1, py: 1.5 } }}
            />
            <IconButton
                onClick={increment}
                sx={{
                    borderRadius: 0,
                    background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                    color: "#fff",
                    width: 48,
                    height: 50,
                    transition: TRANSITION_SMOOTH,
                    "&:hover": { background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)" },
                    "&:active": { transform: "scale(0.95)" },
                }}
            >
                <AddIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */
const INITIAL_TEAM_DATA = {
    id: null,
    value: null,
    label: null,
    frais_transport: null,
    forfait_deplacement: null,
    lunchBreakDuration: null,
    client: null,
    localization: null,
    vfact: null,
    sous_service: null,
    type: null,
    id_country: null,
    settings: [
        { id: null, label: null, value: null, type: null, min: null, max: null },
        { id: null, label: null, value: null, type: null, min: null, max: null },
        { id: null, label: null, value: null, type: null, min: null, max: null },
        { id: null, label: null, value: null, type: null, min: null, max: null },
        { id: null, label: null, value: null, type: null, min: null, max: null },
    ],
    assets: [],
};

export default function MobileTeamsDetailsModal(props) {
    const { visible, setVisible, team, setTeam } = props;
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.settings);

    const [teamData, setTeamData] = useState({ ...INITIAL_TEAM_DATA });
    const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);

    useEffect(() => {
        if (team) {
            setTeamData(team);
        } else {
            setTeamData({ ...INITIAL_TEAM_DATA });
        }
    }, [team]);

    /* ---- Close ---- */
    const onHide = () => {
        setVisible(false);
        setTeam(null);
        setTeamData({ ...INITIAL_TEAM_DATA });
    };

    /* ---- Save ---- */
    const updateTeam = () => {
        if (teamData && teamData.id && teamData.label && teamData.label.trim() !== "") {
            const fields = teamData.settings.map((setting) => ({
                id: setting.id,
                value: parseFloat(setting.value),
            }));

            Promise.all([
                dispatch(
                    editTeam({
                        idTeam: teamData.id,
                        name: teamData.label,
                        sous_service: teamData.sous_service,
                        localization: teamData.localization,
                        lunch_break_duration: teamData.lunchBreakDuration,
                        frais_transport: teamData.frais_transport,
                        forfait_deplacement: teamData.forfait_deplacement,
                        vfact: teamData.vfact,
                        id_country: teamData.id_country,
                    })
                ),
                dispatch(updateTeamSettings({ fields, idTeam: teamData.id })),
            ])
                .then(([editResponse, settingsResponse]) => {
                    const editStatus = editResponse?.payload?.responseData?.status;
                    const settingsStatus = settingsResponse?.payload?.responseData?.status;
                    if (editStatus === 1 && settingsStatus === 1) {
                        showSuccessToast("Équipe modifiée avec succès");
                        dispatch(getTeams());
                        onHide();
                    } else {
                        showErrorToast("An error occurred while updating team details.");
                    }
                })
                .catch((error) => {
                    showErrorToast("An error occurred while updating team details.");
                    console.error("An error occurred while updating team:", error);
                });
        } else {
            showErrorToast("Veuillez remplir le nom du équipe");
        }
    };

    /* ---- Deactivate ---- */
    const deactivateTeam = () => {
        dispatch(
            changeTeamActiveStatus({
                id_team: teamData.id,
                active: 0,
            })
        )
            .then((response) => {
                const responseData = response.payload.responseData;
                if (responseData && responseData.status === 1) {
                    showSuccessToast(Localize(responseData.message));
                    dispatch(getTeams());
                    onHide();
                } else if (responseData && responseData.status === 0) {
                    showErrorToast(Localize(responseData.message));
                }
            })
            .catch((error) => {
                showErrorToast("An error occurred while deactivating the team.");
                console.error("An error occurred while deactivating team:", error);
            });
    };

    /* ---- Setting updater ---- */
    const updateSetting = (index, newValue) => {
        const newSettings = [...teamData.settings];
        newSettings[index] = { ...newSettings[index], value: newValue };
        setTeamData({ ...teamData, settings: newSettings });
    };

    return (
        <>
            <Dialog
                fullScreen
                open={visible}
                onClose={onHide}
                TransitionComponent={Transition}
                sx={{ zIndex: 1400 }}
                PaperProps={{
                    sx: {
                        bgcolor: "rgba(245,246,250,0.97)",
                        backdropFilter: "blur(24px)",
                    },
                }}
            >
                {/* Sticky AppBar */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        bgcolor: "rgba(255,255,255,0.85)",
                        backdropFilter: "blur(24px) saturate(1.5)",
                        WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                        borderBottom: "1px solid rgba(0,0,0,0.04)",
                        color: "text.primary",
                        boxShadow: "0 4px 24px -4px rgba(0,0,0,0.04)",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: "10%",
                            right: "10%",
                            height: "1px",
                            background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.2), transparent)",
                        },
                    }}
                >
                    <Toolbar sx={{ minHeight: 56 }}>
                        <IconButton
                            edge="start"
                            onClick={onHide}
                            sx={{
                                mr: 1,
                                bgcolor: "rgba(0,0,0,0.04)",
                                width: 38,
                                height: 38,
                                borderRadius: "12px",
                                transition: TRANSITION_SMOOTH,
                                "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                                "&:active": { transform: "scale(0.92)" },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 800,
                                fontSize: "1.05rem",
                                letterSpacing: "-0.02em",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                            }}
                        >
                            {team?.label || Localize("TEAM")}
                        </Typography>
                    </Toolbar>
                </AppBar>

                {/* Scrollable content */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                        px: 2,
                        pt: 2.5,
                        pb: "110px",
                    }}
                >
                    {/* === Details section === */}
                    <Box sx={glassCardSx}>
                        <Typography variant="subtitle2" sx={{ ...fieldLabelSx, fontSize: "0.88rem", mb: 2, color: "rgba(255,144,21,0.8)", fontWeight: 800 }}>
                            <Langs str="Details" />
                        </Typography>

                        {/* Nom */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="Nom" />
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Nom"
                            value={teamData.label || ""}
                            onChange={(e) => setTeamData({ ...teamData, label: e.target.value })}
                            sx={glassmorphicTextFieldSx}
                        />

                        {/* Frais transport */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="FRAIS_TRANSPORT" />
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <NumberStepper
                                value={teamData.frais_transport}
                                onChange={(val) => setTeamData({ ...teamData, frais_transport: val })}
                                step={0.1}
                                min={0}
                                currency="EUR"
                            />
                        </Box>

                        {/* Forfait déplacement */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="FORFAIT_DEPLACEMENT" />
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <NumberStepper
                                value={teamData.forfait_deplacement}
                                onChange={(val) => setTeamData({ ...teamData, forfait_deplacement: val })}
                                step={0.1}
                                min={0}
                                currency="EUR"
                            />
                        </Box>

                        {/* Durée pause déjeuner */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="LUNCH_BREAK_DURATION" />
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <NumberStepper
                                value={teamData.lunchBreakDuration}
                                onChange={(val) => setTeamData({ ...teamData, lunchBreakDuration: val })}
                                step={0.1}
                                min={0}
                            />
                        </Box>

                        {/* Client (disabled) */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="CLIENT" />
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            disabled
                            placeholder="Client"
                            value={teamData.client || ""}
                            onChange={(e) => setTeamData({ ...teamData, client: e.target.value })}
                            sx={glassmorphicTextFieldSx}
                        />

                        {/* Sous service */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="SOUS_SERVICE" />
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Sous service"
                            value={teamData.sous_service || ""}
                            onChange={(e) => setTeamData({ ...teamData, sous_service: e.target.value })}
                            sx={glassmorphicTextFieldSx}
                        />

                        {/* vFact */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="vFact" />
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                            maxRows={8}
                            placeholder="vFact"
                            value={teamData.vfact || ""}
                            onChange={(e) => setTeamData({ ...teamData, vfact: e.target.value })}
                            sx={glassmorphicTextFieldSx}
                        />

                        {/* Country */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="country" />
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ ...glassmorphicTextFieldSx }}>
                            <Select
                                value={teamData.id_country || ""}
                                onChange={(e) =>
                                    setTeamData({ ...teamData, id_country: e.target.value })
                                }
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected) return <em style={{ opacity: 0.4 }}>Country...</em>;
                                    const found = (settings.countries || []).find(
                                        (c) => c.value === selected
                                    );
                                    return found ? found.label : selected;
                                }}
                                sx={{
                                    borderRadius: BORDER_RADIUS_INPUT,
                                    fontSize: "16px",
                                    "& .MuiSelect-select": { fontSize: "16px" },
                                }}
                                MenuProps={{ sx: { zIndex: 1500 } }}
                            >
                                {(settings.countries || []).map((c) => (
                                    <MenuItem key={c.value} value={c.value}>
                                        {c.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Localization */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="localization" />
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ ...glassmorphicTextFieldSx }}>
                            <Select
                                value={teamData.localization || ""}
                                onChange={(e) =>
                                    setTeamData({ ...teamData, localization: e.target.value })
                                }
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected)
                                        return <em style={{ opacity: 0.4 }}>Localization...</em>;
                                    return selected;
                                }}
                                sx={{
                                    borderRadius: BORDER_RADIUS_INPUT,
                                    fontSize: "16px",
                                    "& .MuiSelect-select": { fontSize: "16px" },
                                }}
                                MenuProps={{ sx: { zIndex: 1500 } }}
                            >
                                {contractsLocationsOptions.map((loc) => (
                                    <MenuItem key={loc.name} value={loc.name}>
                                        {loc.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* === Settings section === */}
                    <Box sx={glassCardSx}>
                        <Typography variant="subtitle2" sx={{ ...fieldLabelSx, fontSize: "0.88rem", mb: 2, color: "rgba(255,144,21,0.8)", fontWeight: 800 }}>
                            <Langs str="Settings" />
                        </Typography>

                        {teamData.settings.map((setting, index) =>
                            setting.type === "number" ? (
                                <Box key={setting.id || index} sx={{ mb: 2 }}>
                                    <Typography sx={fieldLabelSx}>
                                        <Langs str={setting.label} />
                                    </Typography>
                                    <NumberStepper
                                        value={setting.value}
                                        onChange={(val) => updateSetting(index, val)}
                                        step={1}
                                        min={setting.min}
                                        max={setting.max}
                                        maxFractionDigits={5}
                                    />
                                </Box>
                            ) : null
                        )}
                    </Box>

                    {/* === Assets section === */}
                    <Box sx={glassCardSx}>
                        <AssetsPannel
                            tableName="teams"
                            idParent={teamData?.id}
                            assets={teamData?.assets}
                            uploadCallBackFunction={() => {}}
                            deleteCallBackFunction={() => {}}
                            label="Assets"
                            parentSetter={setTeamData}
                        />
                    </Box>
                </Box>

                {/* Sticky bottom action bar */}
                <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        gap: 1.5,
                        bgcolor: "rgba(255,255,255,0.92)",
                        backdropFilter: "blur(24px) saturate(1.5)",
                        WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                        borderTop: "1px solid rgba(0,0,0,0.04)",
                        boxShadow: "0 -8px 32px -4px rgba(0,0,0,0.04)",
                        zIndex: 1,
                    }}
                >
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setConfirmDeactivateOpen(true)}
                        sx={{
                            borderRadius: "16px",
                            textTransform: "none",
                            fontWeight: 700,
                            minHeight: 50,
                            borderColor: "rgba(211,47,47,0.3)",
                            color: "error.main",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": {
                                borderColor: "error.main",
                                bgcolor: "rgba(211,47,47,0.04)",
                                boxShadow: "0 4px 14px rgba(211,47,47,0.1)",
                            },
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        <Langs str="DEACTIVATE_TEAM" />
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={updateTeam}
                        sx={{
                            borderRadius: "16px",
                            textTransform: "none",
                            fontWeight: 700,
                            minHeight: 50,
                            background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                            color: "#fff",
                            boxShadow: "0 6px 20px rgba(255,144,21,0.35), 0 2px 6px rgba(255,144,21,0.15)",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": {
                                background: "linear-gradient(135deg, #ff7b00 0%, #FF9015 100%)",
                                boxShadow: "0 8px 28px rgba(255,144,21,0.4)",
                            },
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        <Langs str="SAVE" />
                    </Button>
                </Box>
            </Dialog>

            {/* Confirm deactivation dialog */}
            <Dialog
                open={confirmDeactivateOpen}
                onClose={() => setConfirmDeactivateOpen(false)}
                sx={{ zIndex: 1500 }}
                PaperProps={{
                    sx: {
                        borderRadius: "24px",
                        mx: 2,
                        p: 3,
                        maxWidth: 340,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.95)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        boxShadow: "0 24px 80px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.06)",
                    },
                }}
            >
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "16px",
                        background: "linear-gradient(135deg, rgba(255,144,21,0.12) 0%, rgba(255,193,0,0.08) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 1.5,
                    }}
                >
                    <WarningAmberIcon sx={{ fontSize: 32, color: "warning.main" }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.75, letterSpacing: "-0.02em" }}>
                    Delete Confirmation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6, opacity: 0.7 }}>
                    {Localize("DELETE_TEAM_CONFIRM_MESSAGE")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setConfirmDeactivateOpen(false)}
                        sx={{
                            borderRadius: "14px",
                            textTransform: "none",
                            fontWeight: 700,
                            minHeight: 46,
                            borderColor: "rgba(0,0,0,0.12)",
                            transition: TRANSITION_SMOOTH,
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        <Langs str="NO" />
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                            deactivateTeam();
                            setConfirmDeactivateOpen(false);
                        }}
                        sx={{
                            borderRadius: "14px",
                            textTransform: "none",
                            fontWeight: 700,
                            minHeight: 46,
                            bgcolor: "error.main",
                            boxShadow: "0 4px 14px rgba(211,47,47,0.25)",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": { bgcolor: "error.dark" },
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        <Langs str="YES" />
                    </Button>
                </Box>
            </Dialog>
        </>
    );
}
